import { HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { filter, map, Observable, race, switchMap, take, throwError } from 'rxjs';
import { callApi } from '../state-files/action';
import { ApiFailureEntry } from '../state-files/reducer';
import { selectApiData, selectApiFailure } from '../state-files/selectors';

/**
 * How the value cached by the `callApi` effect (`{ data, total }`) is handed back as `body`.
 * - `'unwrap'`: `body` is `cached.data || cached` (the response body itself).
 * - `'envelope'`: `body` is the cached `{ data, total }` object as stored.
 */
export type StoreBodyShape = 'unwrap' | 'envelope';

/** Options for {@link storeBackedRequest}. */
export interface StoreBackedRequestOptions {
  /** HTTP method for the dispatched `callApi`. Defaults to `'POST'`. */
  method?: string;
  /** Store key for the cached result. Defaults to the endpoint path. */
  key?: string;
  /** Shape of the emitted `body`. Defaults to `'unwrap'`. */
  bodyShape?: StoreBodyShape;
}

/**
 * Waits on the `api` store slice for the outcome of a request key: emits the cached data
 * once it is present, or errors as soon as a failure NEWER than `baseline` is recorded for
 * the key. Completes after one value either way.
 *
 * Without the failure branch, `select(selectApiData(key)).pipe(filter(Boolean), take(1))`
 * never emits or errors when the call fails, which is what left pages on "Loading…" forever.
 * @param store - The NgRx store.
 * @param key - Request key the call was (or will be) dispatched with.
 * @param baseline - Failure entry already recorded for the key before this wait began; it is
 *   ignored so a stale failure from an earlier attempt cannot fail this one.
 * @param staleData - Value already cached for the key when a refresh was forced; it is ignored
 *   so the wait emits the refreshed value instead of the old one. Omit for a plain fetch.
 * @returns Observable of the cached value; errors with the recorded error (or a generic Error).
 */
// any: the api.data cache is untyped; callers decide the body type.
export function awaitApiStoreResult(
  store: Store,
  key: string,
  baseline: ApiFailureEntry | undefined,
  staleData?: unknown
): Observable<any> {
  return race(
    store.select(selectApiData(key)).pipe(
      filter((data) => Boolean(data) && data !== staleData),
      take(1)
    ),
    store.select(selectApiFailure(key)).pipe(
      filter((entry): entry is ApiFailureEntry => Boolean(entry) && entry !== baseline),
      take(1),
      switchMap((entry) => throwError(() => entry.error ?? new Error(`API call failed: ${key}`)))
    )
  );
}

/**
 * Store-backed request used by the service wrappers that cache through the `callApi` effect.
 *
 * Behaviour kept from the hand-written wrappers it replaces:
 * - served from `api.data[key]` when present (the key ignores the payload, as before);
 * - otherwise dispatches `callApi` and emits the result as `{ body }` (no `status`), in the
 *   shape chosen by `bodyShape`.
 *
 * - dispatches whenever the store holds nothing for the key, even if an earlier call for the
 *   same key is still pending (deliberately NOT de-duplicated: after a company switch clears
 *   the store, a new caller must issue its own request rather than wait on the old one).
 *
 * The ONLY behaviour change: a failed call ERRORS the observable instead of leaving it pending
 * forever. The caller subscribes to the outcome BEFORE the dispatch, so a failure recorded
 * synchronously during the dispatch cannot be missed.
 * @param store - The NgRx store.
 * @param endpoint - Endpoint path relative to the API base URL.
 * @param payload - Request payload sent when the call has to be dispatched.
 * @param options - Method, cache key and body shape overrides.
 * @returns Cold observable emitting one `{ body }` response and completing, or erroring.
 */
// any: mirrors the wrappers' Observable<HttpResponse<any>> contract over an untyped cache.
export function storeBackedRequest(
  store: Store,
  endpoint: string,
  payload: unknown,
  options: StoreBackedRequestOptions = {}
): Observable<HttpResponse<any>> {
  const key = options.key ?? endpoint;
  const method = options.method ?? 'POST';
  const bodyShape = options.bodyShape ?? 'unwrap';

  return new Observable<any>((subscriber) => {
    let cached: unknown;
    let baseline: ApiFailureEntry | undefined;
    store.select(selectApiData(key)).pipe(take(1)).subscribe((value) => (cached = value));
    store.select(selectApiFailure(key)).pipe(take(1)).subscribe((value) => (baseline = value));

    const outcome = awaitApiStoreResult(store, key, baseline).subscribe(subscriber);

    if (!cached) {
      // any: callApi's payload prop is untyped.
      store.dispatch(callApi({ method, endpoint, payload: payload as any, key }));
    }

    return outcome;
  }).pipe(
    map((data) => {
      const body = bodyShape === 'envelope' ? data : data.data || data;
      return { body } as HttpResponse<any>;
    })
  );
}
